import React, { useState, useEffect } from 'react';
import { X, Search, ExternalLink, MapPin, Users, Video, TrendingUp, Eye, Clock, BarChart3 } from 'lucide-react';
import PlatformIcon from './PlatformIcon';

const CampaignCreatorsList = ({ creators, campaignId, onRemoveCreator, onCreatorUpdated, compact = false, initialOpen = false, onPanelClose }) => {
  const [showPanel, setShowPanel] = useState(initialOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editCost, setEditCost] = useState('');
  const [editCurrency, setEditCurrency] = useState('GBP');
  const [savingId, setSavingId] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorData, setCreatorData] = useState(null);
  const [creatorLoading, setCreatorLoading] = useState(false);

  const filteredCreators = creators.filter(creator =>
    creator?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const maxInitial = compact ? 4 : 5;
  const displayedCreators = filteredCreators.slice(0, maxInitial);
  const sponsoredCount = creators.filter(c => c.isSponsored).length;

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showPanel) { setShowPanel(false); setSelectedCreator(null); onPanelClose?.(); }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showPanel]);

  const handleRemove = async (creatorId, creatorName) => {
    if (!window.confirm(`Remove ${creatorName} from this campaign?`)) return;
    setRemovingId(creatorId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/creators/${creatorId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!response.ok) throw new Error('Failed to remove creator');
      if (onRemoveCreator) onRemoveCreator(creatorId);
    } catch (error) {
      console.error('Error removing creator:', error);
      alert('Failed to remove creator. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggleSponsored = async (creator) => {
    setSavingId(creator.id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/creators/${creator.id}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ isSponsored: !creator.isSponsored })
        }
      );
      if (!response.ok) throw new Error('Failed to update');
      if (onCreatorUpdated) onCreatorUpdated();
    } catch (error) {
      console.error('Error toggling sponsored:', error);
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveCost = async (creator) => {
    setSavingId(creator.id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/creators/${creator.id}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ isSponsored: true, agreedRate: editCost ? parseFloat(editCost) : null, currency: editCurrency })
        }
      );
      if (!response.ok) throw new Error('Failed to save cost');
      setEditingId(null);
      if (onCreatorUpdated) onCreatorUpdated();
    } catch (error) {
      console.error('Error saving cost:', error);
    } finally {
      setSavingId(null);
    }
  };

  const handleSelectCreator = async (creator) => {
    setSelectedCreator(creator);
    setCreatorData(null);
    setCreatorLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/creators/${creator.id}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCreatorData(data.creator || data);
      }
    } catch (err) {
      console.error('Error fetching creator:', err);
    } finally {
      setCreatorLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const CreatorRow = ({ creator, isCompact = false }) => (
    <div className={`flex items-center gap-2 ${isCompact ? 'p-2' : 'p-3'} bg-white/3 rounded-lg hover:bg-white/5 transition group`}>
      <div className="flex-shrink-0">
        {creator.profileImageUrl ? (
          <img src={creator.profileImageUrl} alt={creator.name} className={`${isCompact ? 'w-8 h-8' : 'w-10 h-10'} rounded-full object-cover`} />
        ) : (
          <div className={`${isCompact ? 'w-8 h-8 text-sm' : 'w-10 h-10'} rounded-full bg-sc-pink/15 flex items-center justify-center text-sc-pink font-semibold`}>
            {creator.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className={`font-medium text-white truncate ${isCompact ? 'text-sm' : ''}`} title={creator.name}>
            {creator.name}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!creator.isSponsored) {
                setEditingId(creator.id);
                setEditCost(creator.agreedRate || '');
                setEditCurrency(creator.currency || 'GBP');
              } else {
                handleToggleSponsored(creator);
              }
            }}
            disabled={savingId === creator.id}
            className={`font-bold text-sm leading-none transition-colors ${
              creator.isSponsored ? 'text-green-500 hover:text-green-700' : 'text-white hover:text-green-500'
            }`}
            title={creator.isSponsored ? 'Click to remove sponsored' : 'Click to mark as sponsored'}
          >
            £
          </button>
        </div>
        {creator.isSponsored && creator.agreedRate > 0 && (
          <div className="text-xs text-green-400 font-medium">
            {{ GBP: '£', USD: '$', EUR: '€' }[creator.currency] || '£'}{creator.agreedRate.toLocaleString()}
          </div>
        )}
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={() => handleRemove(creator.id, creator.name)}
          disabled={removingId === creator.id}
          className="opacity-0 group-hover:opacity-100 p-1 text-white hover:text-red-500 transition-opacity flex-shrink-0"
          title="Remove from campaign"
        >
          {removingId === creator.id ? (
            <svg className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} animate-spin`} viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <X className={isCompact ? 'w-3 h-3' : 'w-4 h-4'} />
          )}
        </button>
      </div>
    </div>
  );

  if (!creators || creators.length === 0) {
    return (
      <div className={`bg-[#241d33] rounded-xl border border-white h-full ${compact ? 'p-3' : 'p-6 mb-6'}`}>
        <h3 className={`font-semibold text-white ${compact ? 'text-sm mb-1' : 'text-lg mb-2'}`}>Campaign Creators</h3>
        <p className={`text-white ${compact ? 'text-xs' : 'text-sm'}`}>No creators added yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-[#241d33] rounded-xl border border-white h-full ${compact ? 'p-3' : 'p-4 mb-6'}`}>
        <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
          <h3 className={`font-semibold text-white ${compact ? 'text-sm' : 'text-lg'}`}>
            {compact ? 'Creators' : 'Campaign Creators'} ({creators.length})
            {sponsoredCount > 0 && (
              <span className="ml-2 text-xs font-normal text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full">
                💰 {sponsoredCount} sponsored
              </span>
            )}
          </h3>
          {creators.length > maxInitial && (
            <button
              onClick={() => { setShowPanel(true); setSearchTerm(''); setSelectedCreator(null); }}
              className={`text-sc-pink hover:text-sc-pink/80 font-medium ${compact ? 'text-xs' : 'text-sm'}`}
            >
              All ({creators.length})
            </button>
          )}
        </div>

        <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'}`}>
          {displayedCreators.map((creator) => (
            <CreatorRow key={creator.id} creator={creator} isCompact={compact} />
          ))}
        </div>

        {filteredCreators.length > maxInitial && (
          <div className={`text-center ${compact ? 'mt-2' : 'mt-3'}`}>
            <button
              onClick={() => { setShowPanel(true); setSearchTerm(''); setSelectedCreator(null); }}
              className={`text-white hover:text-sc-pink ${compact ? 'text-xs' : 'text-sm'}`}
            >
              + {filteredCreators.length - maxInitial} more
            </button>
          </div>
        )}
      </div>

      {/* Slide-out Creator Panel - LEFT side */}
      {showPanel && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => { setShowPanel(false); setSelectedCreator(null); onPanelClose?.(); }} />

          {/* Left panel - creator list */}
          <div className="relative w-80 bg-[#1a1525] border-r border-pink-500/15 h-full flex flex-col animate-slide-in-left flex-shrink-0">
            <div className="p-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Creators ({creators.length})</h3>
                <button onClick={() => { setShowPanel(false); setSelectedCreator(null); onPanelClose?.(); }} className="p-1 text-white hover:text-sc-pink transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                <input
                  type="text"
                  placeholder="Search creators..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredCreators.length === 0 ? (
                <p className="text-white text-sm text-center py-8">No creators match "{searchTerm}"</p>
              ) : (
                filteredCreators.map((creator) => (
                  <div
                    key={creator.id}
                    onClick={() => handleSelectCreator(creator)}
                    className={`group flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition ${
                      selectedCreator?.id === creator.id
                        ? 'bg-sc-pink/15 border border-sc-pink/25'
                        : 'bg-white/3 hover:bg-white/8 border border-transparent'
                    }`}
                  >
                    {creator.profileImageUrl ? (
                      <img src={creator.profileImageUrl} alt={creator.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-sc-pink/15 flex items-center justify-center text-sc-pink font-semibold flex-shrink-0 text-sm">
                        {creator.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <p className={`text-sm font-medium truncate ${selectedCreator?.id === creator.id ? 'text-sc-pink' : 'text-white'}`}>{creator.name}</p>
                        {creator.country && (
                          <span className="text-[10px] text-white font-medium flex-shrink-0">{creator.country}</span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!creator.isSponsored) {
                              setEditingId(creator.id);
                              setEditCost(creator.agreedRate || '');
                              setEditCurrency(creator.currency || 'GBP');
                            } else {
                              handleToggleSponsored(creator);
                            }
                          }}
                          disabled={savingId === creator.id}
                          className={`font-bold text-sm leading-none transition-colors flex-shrink-0 ${
                            creator.isSponsored ? 'text-green-500 hover:text-green-700' : 'text-white hover:text-green-500'
                          }`}
                          title={creator.isSponsored ? 'Click to remove sponsored' : 'Click to mark as sponsored'}
                        >
                          £
                        </button>
                      </div>
                      {creator.isSponsored && creator.agreedRate > 0 && (
                        <span className="text-[10px] text-green-400 font-medium">
                          💰 {{ GBP: '£', USD: '$', EUR: '€' }[creator.currency] || '£'}{creator.agreedRate?.toLocaleString() || '0'}
                        </span>
                      )}
                      {editingId === creator.id && (
                        <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={editCurrency}
                            onChange={(e) => setEditCurrency(e.target.value)}
                            className="bg-white/10 border border-white rounded text-[10px] text-white px-1 py-0.5"
                          >
                            <option value="GBP">£</option>
                            <option value="USD">$</option>
                            <option value="EUR">€</option>
                          </select>
                          <input
                            type="number"
                            value={editCost}
                            onChange={(e) => setEditCost(e.target.value)}
                            placeholder="Cost"
                            className="w-16 bg-white/10 border border-white rounded text-[10px] text-white px-1.5 py-0.5"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveCost(creator)}
                            disabled={savingId === creator.id}
                            className="text-[10px] text-green-400 hover:text-green-300 font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-[10px] text-white hover:text-white"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemove(creator.id, creator.name); }}
                      disabled={removingId === creator.id}
                      className="opacity-0 group-hover:opacity-100 p-1 text-white hover:text-red-500 transition-all flex-shrink-0"
                      title="Remove from campaign"
                    >
                      {removingId === creator.id ? (
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right panel - creator profile */}
          {selectedCreator && (
            <div className="relative w-[480px] bg-[#12101e] border-r border-pink-500/15 h-full flex flex-col overflow-y-auto animate-slide-in-left flex-shrink-0">
              {creatorLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-sc-pink/30 border-t-sc-pink rounded-full animate-spin mx-auto" />
                    <p className="text-white text-sm mt-3">Loading profile...</p>
                  </div>
                </div>
              ) : creatorData ? (
                <div className="p-6">
                  {/* Profile Header */}
                  <div className="flex items-center gap-4 mb-6">
                    {creatorData.profileImageUrl ? (
                      <img src={creatorData.profileImageUrl} alt={creatorData.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-sc-pink/20" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-sc-pink/15 flex items-center justify-center text-sc-pink text-2xl font-bold">
                        {creatorData.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white">{creatorData.name}</h4>
                      {creatorData.handle && <p className="text-sm text-white">@{creatorData.handle}</p>}
                      <div className="flex items-center gap-3 mt-1.5">
                        {creatorData.region && (
                          <span className="flex items-center gap-1 text-xs text-white">
                            <MapPin className="w-3 h-3" /> {creatorData.region}
                          </span>
                        )}
                        {creatorData.liveStatus?.isLive && (
                          <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/15 px-2 py-0.5 rounded-full font-medium">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                            LIVE · {formatNumber(creatorData.liveStatus.viewerCount)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sponsored Status */}
                  {selectedCreator.isSponsored && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mb-4">
                      <p className="text-sm text-green-400 font-medium">
                        💰 Sponsored — {{ GBP: '£', USD: '$', EUR: '€' }[selectedCreator.currency] || '£'}{selectedCreator.agreedRate?.toLocaleString() || '0'}
                      </p>
                    </div>
                  )}

                  {/* Audience Stats - per platform */}
                  {creatorData.platformAudience && Object.keys(creatorData.platformAudience).length > 0 && (
                  <div className="mb-5">
                    <h5 className="text-sm font-semibold text-white mb-2">Audience</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {creatorData.platformAudience.youtubeSubscribers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="youtube" size="xs" />
                            <span className="text-[10px] text-white">YouTube Subscribers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.youtubeSubscribers)}</p>
                        </div>
                      )}
                      {creatorData.platformAudience.twitchFollowers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="twitch" size="xs" />
                            <span className="text-[10px] text-white">Twitch Followers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.twitchFollowers)}</p>
                        </div>
                      )}
                      {creatorData.platformAudience.kickSubscribers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="kick" size="xs" />
                            <span className="text-[10px] text-white">Kick Subscribers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.kickSubscribers)}</p>
                        </div>
                      )}
                      {creatorData.platformAudience.tiktokFollowers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="tiktok" size="xs" />
                            <span className="text-[10px] text-white">TikTok Followers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.tiktokFollowers)}</p>
                        </div>
                      )}
                      {creatorData.platformAudience.instagramFollowers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="instagram" size="xs" />
                            <span className="text-[10px] text-white">Instagram Followers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.instagramFollowers)}</p>
                        </div>
                      )}
                      {creatorData.platformAudience.twitterFollowers > 0 && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlatformIcon platform="twitter" size="xs" />
                            <span className="text-[10px] text-white">X Followers</span>
                          </div>
                          <p className="text-lg font-bold text-white">{formatNumber(creatorData.platformAudience.twitterFollowers)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  )}

                  {/* 30-Day Performance Stats - broken down by platform */}
                  {(() => {
                    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                    const recentContent = (creatorData.Content || []).filter(c => c.postDate && new Date(c.postDate) >= thirtyDaysAgo);

                    if (recentContent.length === 0) return null;

                    // Stream stats (Twitch / YouTube live)
                    const streams = recentContent.filter(c => c.contentType === 'stream' || c.contentType === 'vod' || c.streamDuration);
                    const avgCcv = streams.length > 0
                      ? Math.round(streams.reduce((sum, s) => sum + (s.avgCcv || s.streamAvgCcv || 0), 0) / streams.length)
                      : 0;
                    const peakCcv = streams.length > 0
                      ? Math.max(...streams.map(s => s.peakCcv || s.streamPeakCcv || 0))
                      : 0;
                    const totalWatchHours = Math.round(streams.reduce((sum, s) => sum + (s.watchHours || s.streamWatchHours || 0), 0));

                    // Platform-specific video views
                    const videoContent = recentContent.filter(c => !c.streamDuration && c.contentType !== 'stream');
                    const ytVideos = videoContent.filter(c => c.platform?.toLowerCase() === 'youtube');
                    const ttVideos = videoContent.filter(c => c.platform?.toLowerCase() === 'tiktok');
                    const igVideos = videoContent.filter(c => c.platform?.toLowerCase() === 'instagram');

                    const ytAvgViews = ytVideos.length > 0 ? Math.round(ytVideos.reduce((s, v) => s + (v.views || 0), 0) / ytVideos.length) : 0;
                    const ttAvgViews = ttVideos.length > 0 ? Math.round(ttVideos.reduce((s, v) => s + (v.views || 0), 0) / ttVideos.length) : 0;
                    const igAvgViews = igVideos.length > 0 ? Math.round(igVideos.reduce((s, v) => s + (v.views || 0), 0) / igVideos.length) : 0;

                    return (
                      <div className="mb-5">
                        <h5 className="text-sm font-semibold text-white mb-2">30-Day Performance</h5>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Video className="w-3.5 h-3.5 text-cyan-400" />
                              <span className="text-[10px] text-white">Content Pieces</span>
                            </div>
                            <p className="text-lg font-bold text-white">{recentContent.length}</p>
                          </div>

                          {/* Stream stats */}
                          {streams.length > 0 && avgCcv > 0 && (
                            <>
                              <div className="bg-white/5 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <BarChart3 className="w-3.5 h-3.5 text-green-400" />
                                  <span className="text-[10px] text-white">Avg CCV</span>
                                </div>
                                <p className="text-lg font-bold text-white">{formatNumber(avgCcv)}</p>
                                <p className="text-[10px] text-white">{streams.length} stream{streams.length !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="bg-white/5 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                                  <span className="text-[10px] text-white">Peak CCV</span>
                                </div>
                                <p className="text-lg font-bold text-white">{formatNumber(peakCcv)}</p>
                              </div>
                              {totalWatchHours > 0 && (
                                <div className="bg-white/5 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-3.5 h-3.5 text-blue-400" />
                                    <span className="text-[10px] text-white">Watch Hours</span>
                                  </div>
                                  <p className="text-lg font-bold text-white">{formatNumber(totalWatchHours)}</p>
                                </div>
                              )}
                            </>
                          )}

                          {/* YouTube avg views */}
                          {ytAvgViews > 0 && (
                            <div className="bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <PlatformIcon platform="youtube" size="xs" />
                                <span className="text-[10px] text-white">YouTube Avg Views</span>
                              </div>
                              <p className="text-lg font-bold text-white">{formatNumber(ytAvgViews)}</p>
                              <p className="text-[10px] text-white">{ytVideos.length} video{ytVideos.length !== 1 ? 's' : ''} · via YouTube Data API</p>
                            </div>
                          )}

                          {/* TikTok avg views */}
                          {ttAvgViews > 0 && (
                            <div className="bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <PlatformIcon platform="tiktok" size="xs" />
                                <span className="text-[10px] text-white">TikTok Avg Views</span>
                              </div>
                              <p className="text-lg font-bold text-white">{formatNumber(ttAvgViews)}</p>
                              <p className="text-[10px] text-white">{ttVideos.length} video{ttVideos.length !== 1 ? 's' : ''}</p>
                            </div>
                          )}

                          {/* Instagram avg views */}
                          {igAvgViews > 0 && (
                            <div className="bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <PlatformIcon platform="instagram" size="xs" />
                                <span className="text-[10px] text-white">Instagram Avg Views</span>
                              </div>
                              <p className="text-lg font-bold text-white">{formatNumber(igAvgViews)}</p>
                              <p className="text-[10px] text-white">{igVideos.length} post{igVideos.length !== 1 ? 's' : ''}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Platform Links */}
                  <div className="mb-5">
                    <h5 className="text-sm font-semibold text-white mb-2">Platforms</h5>
                    <div className="space-y-1.5">
                      {creatorData.youtubeUrl && (
                        <a href={creatorData.youtubeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="youtube" size="sm" />
                          <span>YouTube</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                      {(creatorData.twitchUrl || creatorData.twitchUsername) && (
                        <a href={creatorData.twitchUrl || `https://twitch.tv/${creatorData.twitchUsername}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="twitch" size="sm" />
                          <span>Twitch</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                      {(creatorData.kickUrl || creatorData.kickSlug) && (
                        <a href={creatorData.kickUrl || `https://kick.com/${creatorData.kickSlug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="kick" size="sm" />
                          <span>Kick</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                      {creatorData.tiktokUrl && (
                        <a href={creatorData.tiktokUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="tiktok" size="sm" />
                          <span>TikTok</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                      {creatorData.instagramUrl && (
                        <a href={creatorData.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="instagram" size="sm" />
                          <span>Instagram</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                      {creatorData.twitterUrl && (
                        <a href={creatorData.twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/8 transition text-sm text-white">
                          <PlatformIcon platform="twitter" size="sm" />
                          <span>X / Twitter</span>
                          <ExternalLink className="w-3 h-3 ml-auto text-white" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Campaigns */}
                  {creatorData.CampaignInfluencer && creatorData.CampaignInfluencer.length > 0 && (
                    <div className="mb-5">
                      <h5 className="text-sm font-semibold text-white mb-2">Campaigns ({creatorData.CampaignInfluencer.length})</h5>
                      <div className="space-y-1.5">
                        {creatorData.CampaignInfluencer.map((ci) => (
                          <div key={ci.Campaign?.id || ci.id} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg text-sm">
                            <span className="text-white truncate">{ci.Campaign?.name || 'Unknown'}</span>
                            {ci.isSponsored && <span className="text-green-400 text-[10px]">💰</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Content */}
                  {creatorData.Content && creatorData.Content.length > 0 && (
                    <div className="mb-5">
                      <h5 className="text-sm font-semibold text-white mb-2">Recent Content</h5>
                      <div className="space-y-2">
                        {creatorData.Content.slice(0, 5).map((content) => (
                          <a
                            key={content.id}
                            href={content.contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-2 bg-white/3 rounded-lg hover:bg-white/5 transition"
                          >
                            {content.thumbnailUrl ? (
                              <img src={content.thumbnailUrl} alt="" className="w-16 h-10 rounded object-cover flex-shrink-0 bg-white/5" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-16 h-10 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                                <Video className="w-4 h-4 text-white/20" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-white truncate">{content.title || 'Untitled'}</p>
                              <div className="flex items-center gap-2 text-[10px] text-white">
                                {content.views > 0 && <span>{formatNumber(content.views)} views</span>}
                                {(content.avgCcv || content.streamAvgCcv) > 0 && <span>· {formatNumber(content.avgCcv || content.streamAvgCcv)} avg CCV</span>}
                                {content.postDate && <span>· {new Date(content.postDate).toLocaleDateString()}</span>}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View Full Profile Link */}
                  <div className="mt-4">
                    <a
                      href={`/dashboard/creators/${selectedCreator.id}`}
                      className="block text-center px-4 py-2.5 bg-sc-pink/15 border border-sc-pink/25 text-sc-pink rounded-lg hover:bg-sc-pink/25 text-sm font-medium transition-colors"
                    >
                      View Full Profile →
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Cost Edit Modal */}
      {editingId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#241d33] border border-white rounded-xl p-5 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-white mb-3">Set Sponsored Cost</h3>
            <p className="text-sm text-white mb-3">{creators.find(c => c.id === editingId)?.name}</p>
            <div className="flex items-center gap-2 mb-4">
              <select
                value={editCurrency}
                onChange={(e) => setEditCurrency(e.target.value)}
                className="px-2 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
              >
                <option value="GBP">£ GBP</option>
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
              </select>
              <input
                type="number"
                value={editCost}
                onChange={(e) => setEditCost(e.target.value)}
                placeholder="0.00"
                className="flex-1 px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCost(creators.find(c => c.id === editingId)); }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-white border border-white rounded-lg hover:bg-white/5">Cancel</button>
              <button
                onClick={() => handleSaveCost(creators.find(c => c.id === editingId))}
                disabled={savingId}
                className="px-3 py-1.5 text-sm btn-pink disabled:opacity-50"
              >
                {savingId ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-left {
          animation: slideInLeft 0.25s ease-out;
        }
      `}</style>
    </>
  );
};

export default CampaignCreatorsList;